/**
 * useStaffPref (A40) — персональная настройка сотрудника: серверное
 * хранение staff_prefs (переживает смену устройства) + мгновенный
 * localStorage-кеш ub_pref_{staffId}_{key} для первого рендера.
 *
 * Протокол: маунт → кеш сразу в state → GET /api/staff-prefs/:key
 * (серверное значение перекрывает кеш, ЕСЛИ пользователь ещё не правил —
 * dirty-гард, И нет незавершённого PUT ключа — реестр inflightPuts закрывает
 * гонку GET-после-PUT между маунтами). Сеттер:
 * state + кеш немедленно, PUT — с дебаунсом 600мс (drag шлёт порядок
 * очередью, серверу летит последний); незалитый PUT доотправляется при
 * анмаунте И на pagehide/сворачивании (TMA-WebView убивают страницу без
 * unmount-cleanup — стандарт UndoSnackbar), fetch с keepalive — переживает
 * выгрузку (прецедент useBranchConfig). Ошибки сети/404 глушатся: staff-prefs
 * может ещё не быть задеплоен (PRC-1) — хук живёт на localStorage.
 *
 * Wire-контракт (PRC-1): GET /api/staff-prefs/:key → { value: T | null };
 * PUT /api/staff-prefs/:key body { value: T } (JSON, от auth.staffId).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth";

const PUT_DEBOUNCE_MS = 600;

/** Незавершённые PUT по `${staffId}:${key}` — module-scope, переживает
 *  ремаунт хука: dirty-гард живёт внутри одного маунта, а гонка GET-после-PUT
 *  случается МЕЖДУ маунтами (ушёл с суб-таба → flush PUT → вернулся <1с:
 *  GET нового маунта обгоняет PUT и откатил бы свежий drag-порядок). */
const inflightPuts = new Map<string, Promise<unknown>>();

const lsKey = (staffId: string, key: string): string => `ub_pref_${staffId}_${key}`;

function readCache<T>(k: string): { v: T } | null {
  try {
    const raw = localStorage.getItem(k);
    return raw == null ? null : { v: JSON.parse(raw) as T };
  } catch {
    return null;
  }
}

function writeCache(k: string, v: unknown): void {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* квота/недоступно — не критично, останется серверная копия */
  }
}

export function useStaffPref<T>(key: string, fallback: T): [T, (v: T) => void] {
  const { user } = useAuth();
  const staffId = user?.staffId ?? "";
  const [val, setVal] = useState<T>(() => {
    const c = staffId ? readCache<T>(lsKey(staffId, key)) : null;
    return c ? c.v : fallback;
  });
  const dirtyRef = useRef(false);
  const putT = useRef(0);
  const pendingRef = useRef<{ v: T } | null>(null);
  const idsRef = useRef({ staffId, key });
  idsRef.current = { staffId, key };

  const flush = useCallback(() => {
    window.clearTimeout(putT.current);
    const p = pendingRef.current;
    if (!p || !idsRef.current.staffId) return;
    pendingRef.current = null;
    /* keepalive: flush зовётся и на pagehide/сворачивании — PUT обязан
       пережить выгрузку страницы (стандарт A43.2, ср. useBranchConfig). */
    const ik = `${idsRef.current.staffId}:${idsRef.current.key}`;
    const put = api(`/api/staff-prefs/${encodeURIComponent(idsRef.current.key)}`, {
      method: "PUT",
      body: { value: p.v },
      keepalive: true,
    }).catch(() => undefined);
    inflightPuts.set(ik, put);
    void put.finally(() => { if (inflightPuts.get(ik) === put) inflightPuts.delete(ik); });
  }, []);

  /* Маунт/смена staffId: кеш мгновенно, затем сервер (если не правили). */
  useEffect(() => {
    if (!staffId) return;
    dirtyRef.current = false;
    const c = readCache<T>(lsKey(staffId, key));
    if (c) setVal(c.v);
    let alive = true;
    api<{ value: T | null }>(`/api/staff-prefs/${encodeURIComponent(key)}`)
      .then((r) => {
        if (!alive || dirtyRef.current || r.value == null) return;
        /* PUT этого ключа ещё в полёте (прошлый маунт) — любой GET-ответ
           старее него; кеш уже держит новейшее значение, ответ игнорируем. */
        if (inflightPuts.has(`${staffId}:${key}`)) return;
        setVal(r.value);
        writeCache(lsKey(staffId, key), r.value);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [staffId, key]);

  /* Анмаунт И pagehide/сворачивание (WebView убивают страницу без
     unmount-cleanup): доотправить неулетевший PUT — иначе порядок жил бы
     только в localStorage этого устройства (ровно то, от чего A40 защищает). */
  useEffect(() => {
    const vis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", vis);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", vis);
      flush();
    };
  }, [flush]);

  const set = useCallback(
    (v: T) => {
      dirtyRef.current = true;
      setVal(v);
      const { staffId: sid } = idsRef.current;
      if (!sid) return;
      writeCache(lsKey(sid, idsRef.current.key), v);
      pendingRef.current = { v };
      window.clearTimeout(putT.current);
      putT.current = window.setTimeout(flush, PUT_DEBOUNCE_MS);
    },
    [flush],
  );

  return [val, set];
}
